import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import DissolvingWindow from '@/Components/Marketing/DissolvingWindow';
import LazyBoundary from '@/Components/Marketing/LazyBoundary';
import { demoHref } from '@/Components/Marketing/links';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import { BEHIND_CARDS_OPACITY, Cloud, FULL_WIDTH_ZONE } from '@/Components/Marketing/Texture';
import { Heading } from '@/Components/Marketing/Typography';
import useMediaQuery from '@/Components/Marketing/useMediaQuery';
import useNearViewport from '@/Components/Marketing/useNearViewport';
import PlaybackGate from '@/Components/Marketing/Vignettes/PlaybackGate';
import { cn } from '@/lib/utils';

// The cells' vignettes sit below the fold and are loaded as they near the
// viewport: one small chunk first (it maps a cell to its vignette), then each
// vignette's own. One loader per cell, so each is loaded (and retried) alone.
const loadBentoVignettes = () => import('@/Components/Marketing/Welcome/BentoVignette');

// Where a pointer can hover (the `fine:` variant's query), a cell plays only
// while it is hovered: at rest otherwise, and a pass under way when the
// pointer leaves runs to its end (PlaybackGate). Touch screens, with no
// hover, play each cell while it is on screen, as before (2026-10-04).
export const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

// Each tint is the cell's fill and the colour of its hairline. No orange in
// the cells themselves (R87, R2-5): they stay neutral, the large one and Lab
// sunken, the rest white; the orange is the cloud behind them (2026-10-13).
const TINTS = {
    sunken: 'bg-mk-sunken ring-mk-ink/5',
    white: 'bg-mk-surface ring-mk-line',
};

// Desktop: a 6-column grid. The modelling cell takes 4 columns and both rows
// beside the two supplier/import cells; publish and Lab share the last row,
// with a taller window (their vignettes glide down through their story).
// Tablet: modelling full width, the rest in pairs. Phone: one column.
const CELLS = [
    { key: 'model', tint: 'sunken', span: 'md:col-span-2 lg:col-span-4 lg:row-span-2', large: true },
    { key: 'suppliers', tint: 'white', span: 'lg:col-span-2' },
    { key: 'import', tint: 'white', span: 'lg:col-span-2' },
    { key: 'publish', tint: 'white', span: 'lg:col-span-3', tall: true },
    { key: 'lab', tint: 'sunken', span: 'lg:col-span-3', tall: true },
];

/**
 * What each vignette is laid out as in its cell, from the grid above (the
 * modelling cell is wide from md up; below md it is a phone column), and
 * where its window starts to dissolve. `readable` keeps a vignette's content
 * to that share of its width, inside the part of the window drawn whole.
 * Every width is a desktop one, a phone column's too (drawn smaller there):
 * a stage lays the app out at desktop breakpoints, which a narrower layout
 * would crowd (VignetteFrame).
 */
function useCellLayouts() {
    const lg = useMediaQuery('(min-width: 1024px)');
    const xl = useMediaQuery('(min-width: 1280px)');

    return {
        // The large cell dissolves later. From lg to xl it is narrower but
        // just as tall: drawn larger, the list fills more of it.
        model: { fade: [78, 66], vignette: { baseWidth: lg && !xl ? 760 : 880, readable: 0.8 } },
        suppliers: { vignette: { readable: 0.75 } },
        // Wide enough for the run's Created, Completed and Run time on one line.
        import: { vignette: { readable: 0.85 } },
        publish: { vignette: { readable: 0.75 } },
        // Laid out wider and dissolving later: the bulk edit's preview, as
        // wide as the editor and its after column included, stays where the
        // window is drawn whole.
        lab: { fade: [84, 60], vignette: { baseWidth: 640, readable: 0.82 } },
    };
}

/** Each cell's vignette, by the cell's key (see BentoVignette's loadVignette). */
const VIGNETTE_LOADERS = Object.fromEntries(CELLS.map(({ key }) => [
    key,
    () => loadBentoVignettes().then((module) => module.loadVignette(key)),
]));

/**
 * A cell's window onto the app (a DissolvingWindow), whose vignette loads as
 * the cell nears the viewport. The window's white plate is the placeholder:
 * it has its final size (and dissolves the same way) before the chunk
 * arrives, so nothing moves when it does; if the chunk fails, the plate
 * stays (LazyBoundary).
 */
function CellWindow({ cell, layout }) {
    const [ref, near] = useNearViewport();
    const [fadeX, fadeY] = layout.fade ?? [];

    return (
        <DissolvingWindow ref={ref} data-vignette-window={cell.key} fadeX={fadeX} fadeY={fadeY}>
            {near && (
                <LazyBoundary load={VIGNETTE_LOADERS[cell.key]}>
                    {(Vignette) => <Vignette {...layout.vignette} />}
                </LazyBoundary>
            )}
        </DissolvingWindow>
    );
}

/**
 * Where the cloud behind the cells may show (Texture's Cloud, 2026-10-13:
 * "a bit too blank still"): the page's width, from the heading's row (ink
 * only) to the cells' foot, faded in and out, so it fills the empty side of
 * the heading's row and glows through the gaps and the margins, and stops
 * short of the buttons under the cells (their rings need the section's own
 * white for 3:1).
 */
const CLOUD_ZONE = `-z-10 ${FULL_WIDTH_ZONE} -top-40 bottom-0 [mask-image:linear-gradient(to_bottom,transparent,#000_15%,#000_75%,transparent)]`;

/** The cloud in its zone: its heart over the cells' top right, beside the heading. */
const CLOUD = 'right-[calc(50%-30rem)] top-0 w-[40rem] sm:right-[calc(50%-46rem)] sm:w-[52rem]';

/**
 * The platform in five cells, the app on top: modelling, supplier requests,
 * bulk import, the public QR page and Lab. Each cell shows a live vignette
 * of that part of the product in a window inset from the top left that
 * dissolves into the cell before its right and bottom edges, played while
 * the cell is hovered (HOVER_QUERY). The old site's orange cloud glows
 * behind them.
 */
export default function PlatformBento() {
    const { formatMessage } = useIntl();
    const t = id => formatMessage({ id });
    const headingId = useId();
    const layouts = useCellLayouts();
    const canHover = useMediaQuery(HOVER_QUERY);
    const [hovered, setHovered] = useState(null);

    return (
        <Section tone="surface" aria-labelledby={headingId} className="relative isolate overflow-hidden border-t border-mk-line">
            <Reveal>
                <Heading id={headingId} className="max-w-3xl">
                    {t('site.home.bento.title')}
                </Heading>
            </Reveal>

            <div className="relative mt-12 sm:mt-16">
                <Cloud zone={CLOUD_ZONE} className={CLOUD} opacity={BEHIND_CARDS_OPACITY} />
                <ul role="list" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
                    {CELLS.map((cell, index) => (
                        <Reveal as="li" key={cell.key} delay={(index % 3) * 0.06} className={cn('flex', cell.span)}>
                            {/* One even hairline all round, and a light top edge. */}
                            <article
                                data-bento-cell={cell.key}
                                onPointerEnter={(event) => event.pointerType !== 'touch' && setHovered(cell.key)}
                                onPointerLeave={() => setHovered((current) => (current === cell.key ? null : current))}
                                className={cn(
                                    'flex w-full flex-col overflow-hidden rounded-mk-card ring-1 ring-inset',
                                    'shadow-[inset_0_1px_0_rgb(255_255_255/0.8)]',
                                    TINTS[cell.tint],
                                )}
                            >
                                <div
                                    className={cn(
                                        'relative h-56 sm:h-64',
                                        cell.large && 'sm:h-80 lg:h-auto lg:min-h-[24rem] lg:flex-1',
                                        cell.tall && 'lg:h-80',
                                    )}
                                >
                                    <PlaybackGate open={!canHover || hovered === cell.key}>
                                        <CellWindow cell={cell} layout={layouts[cell.key]} />
                                    </PlaybackGate>
                                </div>
                                <div className="px-6 pb-7 pt-5 sm:px-8 sm:pb-8">
                                    <h3
                                        className={cn(
                                            'text-lg font-semibold tracking-[-0.02em] text-mk-ink',
                                            cell.large && 'sm:text-xl',
                                        )}
                                    >
                                        {t(`site.home.bento.${cell.key}.title`)}
                                    </h3>
                                    <p className="mt-2 max-w-[48ch] text-[15px] leading-relaxed text-pretty text-mk-body">
                                        {t(`site.home.bento.${cell.key}.text`)}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </ul>
            </div>

            {/* The way on, under the cells and aligned to their edges: the
                line, the platform page, a demo. On the section's own white,
                with no fill, ring or corners: drawn as a sunken row at a
                cell's size, it read as a cell whose vignette never came
                (2026-10-05), and a text link alone there was missed. */}
            <Reveal className="mt-10 sm:mt-12">
                <div
                    data-bento-cta=""
                    className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10"
                >
                    <p className="max-w-[36ch] text-xl font-semibold leading-snug tracking-[-0.02em] text-balance text-mk-ink sm:text-2xl">
                        {t('site.home.bento.cta')}
                    </p>
                    <div className="flex shrink-0 flex-wrap gap-3">
                        <Button href={route('outer.platform')} size="lg">
                            {t('site.home.bento.link')}
                        </Button>
                        <Button href={demoHref()} variant="secondary" size="lg">
                            {t('site.cta.demo')}
                        </Button>
                    </div>
                </div>
            </Reveal>
        </Section>
    );
}
